/**
 * Pre-import reconciliation for the "Rebuild from QuickBooks" bundle.
 *
 * Runs ENTIRELY client-side, before a single byte is uploaded: the import
 * modal already holds every selected file's full text in memory, and all the
 * parsers are pure. The goal is to catch bad QuickBooks exports — the class
 * of failure that produced the Parkside incident, where `Journal 2024.CSV`
 * silently contained only January and the books imported $78k off — BEFORE
 * the workflow tears down the previous import and posts 12k entries.
 *
 * Every blocking finding carries a `fix`: the EXACT QuickBooks steps to
 * produce a corrected export and which uploaded file to replace. The modal
 * keeps the file list mounted, so the user swaps one file and re-checks —
 * nothing re-imports, nothing re-uploads.
 *
 * Checks, in order of how decisively they catch a bad export:
 *   1. Month-coverage continuity — any interior month with zero journal
 *      transactions means a year file is truncated or missing.
 *   2. Journal ↔ Customer Balance Detail monthly AR tie-out — the detail
 *      report is a complete AR history, so any month where the journal's
 *      net AR movement disagrees means journal rows are missing/extra.
 *   3. Journal ↔ Vendor Balance Detail monthly AP tie-out — same, for AP.
 *   4. IIF ending-balance tie-out — `ACCNT` rows carry QB's as-of-export
 *      balances (OBAMOUNT); the journal's per-account ending sums must match
 *      for BANK/AR/AP accounts.
 *   5. Cross-file duplicate Trans # — identical duplicates are normal
 *      (year exports overlap into the next January) but content that DIFFERS
 *      under the same Trans # would import nondeterministically.
 */

import { buildIifCustomerRow } from '@/utils/iif-customer-row';
import { type IIFRow, parseIIF } from '@/utils/iif-parser';
import { type ParsedEntry, parseAccountCell, parseCsv, parseJournalRows, parseMoneyToCents } from '@/utils/journal-csv-parser';
import { buildCustomerImportRows } from '@/utils/qb-customer-rows';
import { parseTransactionsCsv } from '@/utils/transactions-csv-parser';
import { parseVendorCsv } from '@/utils/vendor-csv-parser';

export type QbPreflightSeverity = 'info' | 'warning' | 'blocking';

export interface QbPreflightFinding {
	severity: QbPreflightSeverity;
	/** Stable machine code, e.g. 'coverage_gap', 'ar_mismatch'. */
	code: string;
	title: string;
	detail?: string;
	/** Exact remediation steps (QuickBooks export path + which file to replace). */
	fix?: string;
}

export interface QbPreflightFileSummary {
	name: string;
	type: string;
	/** Journal: transaction count. Detail reports: row count. IIF: account count. */
	count: number;
	errors: string[];
	/** First/last transaction month ('YYYY-MM'), journal + detail files. */
	firstMonth?: string;
	lastMonth?: string;
}

export interface QbPreflightDetailStats {
	/** Transaction rows parsed from the detail report. */
	rows: number;
	/** Customer/vendor sections in the report. */
	parties: number;
	/** Parties whose transactions net to a non-zero open balance. */
	partiesWithBalance: number;
	/** Net open balance across all parties (AR: receivable; AP: payable). */
	openBalanceCents: number;
}

export interface QbPreflightBankStat {
	code: string;
	name: string;
	/** Ending balance the journal will project for this account. */
	journalCents: number;
	/** QuickBooks' as-of-export balance from the IIF (OBAMOUNT). */
	qbCents: number;
	matches: boolean;
}

export interface QbPreflightStats {
	journal: {
		uniqueTransactions: number;
		duplicateTransactions: number;
		/** Count per QB type — Invoice / Payment / Bill / Deposit / … */
		byType: Record<string, number>;
		firstMonth?: string;
		lastMonth?: string;
	};
	ar?: QbPreflightDetailStats;
	ap?: QbPreflightDetailStats;
	/** Estimated ending balance per bank account — present when an IIF is included. */
	banks?: QbPreflightBankStat[];
	/** Structure the IIF lists will create — present when an IIF is included. */
	structure?: {
		glAccounts: number;
		customers: number;
		vendors: number;
		/** Vendors carrying plaintext TAXIDs — importing them requires the
		 *  secure-unlock context so EINs are encrypted before upload. */
		vendorTaxIds: number;
	};
	/** Detail rows whose Trans # is MISSING from the journal — exactly what
	 *  auto-correction would synthesize. Drives the "Apply auto-corrections
	 *  (N records)" button. */
	correctable?: { ar: number; ap: number };
}

export interface QbPreflightReport {
	verdict: 'ok' | 'warnings' | 'blocked';
	findings: QbPreflightFinding[];
	files: QbPreflightFileSummary[];
	stats: QbPreflightStats;
	journal: {
		uniqueTransactions: number;
		duplicateTransactions: number;
		months: Record<string, number>;
	};
}

export interface QbPreflightFile {
	name: string;
	/** classifyQbFile type — journal_csv | transactions_csv | vendor_transactions_csv | iif | unknown */
	type: string;
	text: string;
}

const CENTS_TOLERANCE = 50;

const JOURNAL_EXPORT_PATH = 'Reports → Accountant & Taxes → Journal → Customize Report';
const REPLACE_HINT = 'Then click "Back to files", replace the affected file with the new export, and Check files again — your other files stay loaded and nothing has been imported.';

const fmtUsd = (cents: number): string => {
	const sign = cents < 0 ? '-' : '';
	const abs = Math.abs(cents);
	return `${sign}$${(abs / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** 'YYYY-MM' from a UTC-midnight business-date ms. */
const monthKeyFromMs = (ms: number): string => {
	const d = new Date(ms);
	return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

/** 'YYYY-MM' straight from a QB "M/D/YYYY" string — no timezone in play. */
const monthKeyFromDateStr = (s: string): string | null => {
	const m = s.trim().match(/^(\d{1,2})\/\d{1,2}\/(\d{4})$/);
	if (!m) return null;
	return `${m[2]}-${m[1].padStart(2, '0')}`;
};

const monthLabel = (key: string): string => {
	const [y, m] = key.split('-').map(Number);
	return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const nextMonth = (key: string): string => {
	const [y, m] = key.split('-').map(Number);
	return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
};

const prevMonth = (key: string): string => {
	const [y, m] = key.split('-').map(Number);
	return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
};

/**
 * Signature of a journal entry for duplicate-content comparison. Includes the
 * per-line name + memo, not just amounts/accounts: the importer dedups by
 * Trans # (INSERT OR IGNORE), so whichever file stages first controls
 * account/vendor mapping and memo-driven projection — a same-amounts but
 * different-name duplicate is a real conflict, not benign overlap.
 */
const entrySignature = (e: ParsedEntry): string =>
	JSON.stringify([
		e.type, e.postedOn, e.num,
		e.lines.map((l) => [l.accountCode, l.accountName, l.debitCents, l.creditCents, l.name, l.memo]),
	]);

interface IifAccount {
	name: string;
	code: string;
	accountType: string;
	balanceCents: number;
}

/**
 * Minimal IIF `ACCNT` reader — just enough to extract QB's per-account
 * as-of-export balances (OBAMOUNT) for the preflight tie-out. The real lists
 * import happens elsewhere; this never writes anything.
 */
export function parseIifAccountBalances(text: string): IifAccount[] {
	const out: IifAccount[] = [];
	let cols: Record<string, number> | null = null;
	for (const raw of text.split(/\r?\n/)) {
		if (raw.startsWith('!ACCNT')) {
			const map: Record<string, number> = {};
			raw.split('\t').forEach((c, i) => { map[c.trim().toUpperCase()] = i; });
			cols = map;
			continue;
		}
		if (!cols || !raw.startsWith('ACCNT\t')) continue;
		const colMap = cols;
		const cells = raw.split('\t');
		const pick = (key: string): string => {
			const idx = colMap[key];
			return idx == null ? '' : (cells[idx] ?? '').trim();
		};
		const name = pick('NAME');
		if (!name) continue;
		out.push({
			name,
			code: pick('ACCNUM'),
			accountType: pick('ACCNTTYPE').toUpperCase(),
			balanceCents: parseMoneyToCents(pick('OBAMOUNT')),
		});
	}
	return out;
}

interface MonthlyDiff {
	month: string;
	journalCents: number;
	detailCents: number;
}

/**
 * Compare the journal's net movement on a set of GL codes against a detail
 * report's signed monthly sums. Everything is normalized to the DETAIL
 * report's convention (positive AR/AP balances): `sign` is +1 for AR
 * (invoices debit AR, so journal debit−credit already matches) and −1 for AP
 * (bills CREDIT AP, so the journal's net is negated to compare).
 */
const monthlyTieOut = (
	journalByMonth: Map<string, number>,
	detailByMonth: Map<string, number>,
	sign: 1 | -1,
): { mismatches: MonthlyDiff[]; journalTotal: number; detailTotal: number } => {
	const months = new Set([...journalByMonth.keys(), ...detailByMonth.keys()]);
	const mismatches: MonthlyDiff[] = [];
	let journalTotal = 0;
	let detailTotal = 0;
	for (const month of [...months].sort()) {
		const j = sign * (journalByMonth.get(month) ?? 0);
		const d = detailByMonth.get(month) ?? 0;
		journalTotal += j;
		detailTotal += d;
		if (Math.abs(j - d) > CENTS_TOLERANCE) {
			mismatches.push({ month, journalCents: j, detailCents: d });
		}
	}
	return { mismatches, journalTotal, detailTotal };
};

const describeMismatches = (mismatches: MonthlyDiff[], journalTotal: number, detailTotal: number, label: string): string => {
	const shown = mismatches.slice(0, 6)
		.map((m) => `${monthLabel(m.month)}: journal ${fmtUsd(m.journalCents)} vs ${label} ${fmtUsd(m.detailCents)}`);
	const more = mismatches.length > shown.length ? ` (+${mismatches.length - shown.length} more months)` : '';
	return `${shown.join('; ')}${more}. Overall: journal projects ${fmtUsd(journalTotal)}, QuickBooks says ${fmtUsd(detailTotal)}.`;
};

/**
 * Remediation for tie-out mismatches: split the mismatched months into
 * (a) months BEFORE the journal's coverage (backdated history a date-ranged
 * export missed), (b) months inside a known coverage gap (fix that first),
 * and (c) covered-but-wrong months (re-export those months).
 */
const buildMismatchFix = (
	mismatches: MonthlyDiff[],
	journalMonths: string[],
	gapMonths: Set<string>,
): string => {
	const journalMin = journalMonths[0];
	const steps: string[] = [];
	const preRange = journalMin ? mismatches.filter((m) => m.month < journalMin) : [];
	const inGap = mismatches.filter((m) => gapMonths.has(m.month));
	const covered = mismatches.filter((m) => (!journalMin || m.month >= journalMin) && !gapMonths.has(m.month));

	if (inGap.length > 0) {
		steps.push('Most of this is the journal coverage gap above — fix that first and these months will re-check automatically.');
	}
	if (preRange.length > 0) {
		const earliest = preRange[0].month;
		const [y, m] = prevMonth(journalMin).split('-');
		steps.push(`QuickBooks has activity BEFORE your earliest journal export (${monthLabel(earliest)} vs ${monthLabel(journalMin)}): in QuickBooks, ${JOURNAL_EXPORT_PATH}, set the date range to 01/01/${earliest.split('-')[0]} – ${m}/${lastDayOf(prevMonth(journalMin))}/${y}, export to CSV, and ADD it as another journal file here.`);
	}
	if (covered.length > 0) {
		const months = covered.slice(0, 4).map((c) => monthLabel(c.month)).join(', ');
		steps.push(`Re-export the journal covering ${months}${covered.length > 4 ? ` (+${covered.length - 4} more)` : ''} from QuickBooks (${JOURNAL_EXPORT_PATH} → full month range → export to CSV) and replace the file that should contain those months.`);
	}
	steps.push(REPLACE_HINT);
	return steps.join(' ');
};

const lastDayOf = (monthKey: string): number => {
	const [y, m] = monthKey.split('-').map(Number);
	return new Date(Date.UTC(y, m, 0)).getUTCDate();
};

/** Customers sharing one unit address before it reads as a builder/office address, not a unit. */
const SHARED_ADDRESS_MIN = 3;

const listNames = (names: string[]): string => {
	const shown = names.slice(0, 8);
	return `${shown.join('; ')}${names.length > shown.length ? ` (+${names.length - shown.length} more)` : ''}`;
};

/**
 * Unit-address problems in an IIF customer list (#635) — warnings, not blocks: the
 * import is still right to run, but the accounts it creates need a person's eye.
 *
 *   - Customers with NO ship-to (unit) address. The importer leaves their address
 *     empty rather than copying the bill-to block, which for builder-owned units is
 *     the builder's office.
 *   - One unit address shared by several DIFFERENT units — almost always an office
 *     or builder address typed into ship-to. Counted by account number, not name,
 *     so a unit's own sale history (closed seller + active buyer) doesn't trip it.
 */
const customerAddressFindings = (fileName: string, customers: IIFRow[]): QbPreflightFinding[] => {
	const findings: QbPreflightFinding[] = [];
	const missing: string[] = [];
	const fromBillTo: string[] = [];
	const closedNoDate: string[] = [];
	const byAddress = new Map<string, { address: string; units: Set<string>; names: string[] }>();
	for (const cust of customers) {
		const row = buildIifCustomerRow({ cust });
		if (!row) continue;
		const name = row.externalName ?? row.rawName;
		if (row.closed && row.closedOn == null) closedNoDate.push(name);
		if (row.addressMissing) {
			missing.push(name);
			continue;
		}
		if (row.addressFromBillTo) fromBillTo.push(name);
		const key = (row.address ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
		const group = byAddress.get(key) ?? { address: row.address ?? '', units: new Set<string>(), names: [] };
		group.units.add(row.accountNumber ?? name);
		group.names.push(name);
		byAddress.set(key, group);
	}
	if (missing.length > 0) {
		findings.push({
			severity: 'warning',
			code: 'customer_address_missing',
			title: `${fileName}: ${missing.length} customer${missing.length === 1 ? ' has' : 's have'} no unit (ship-to) address`,
			detail: `These accounts will be created with an EMPTY property address — their bill-to address goes to the mailing address only. ${listNames(missing)}`,
			fix: 'Fill in the property address on each account after the import, or add the ship-to address in QuickBooks (Customer → Address Info → Ship To) and re-export the IIF.',
		});
	}
	for (const group of byAddress.values()) {
		if (group.units.size < SHARED_ADDRESS_MIN) continue;
		findings.push({
			severity: 'warning',
			code: 'customer_address_shared',
			title: `${fileName}: ${group.units.size} different units share one property address`,
			detail: `"${group.address.replace(/\n/g, ', ')}" is the unit address of ${listNames(group.names)}. This is usually an office or builder address typed into ship-to.`,
			fix: 'Correct the ship-to address on these customers in QuickBooks and re-export the IIF, or fix each account\'s property address after the import.',
		});
	}

	// Numbering and identity — decided BEFORE anything is written (utils/qb-customer-rows).
	const { skippedNames, notes } = buildCustomerImportRows(customers);
	if (skippedNames.length > 0) {
		findings.push({
			severity: 'warning',
			code: 'customer_no_unit_number',
			title: `${fileName}: ${skippedNames.length} customer${skippedNames.length === 1 ? ' has' : 's have'} no unit number — not imported`,
			detail: listNames(skippedNames),
			fix: 'In QuickBooks, rename each to "<unit number> - <name>" (e.g. "4827ES - Smith") and re-export the IIF — a customer with no unit number cannot be matched to an account.',
		});
	}
	const reversed = notes.filter((n) => n.kind === 'office-numbering');
	if (reversed.length > 0) {
		findings.push({
			severity: 'info',
			code: 'customer_office_numbering',
			title: `${fileName}: ${reversed.length} unit${reversed.length === 1 ? '' : 's'} renumbered to office numbering (live owner on the bare number)`,
			detail: reversed.map((n) => n.message).join('; '),
			fix: 'Nothing to do — later imports match these customers by their QuickBooks id / name, not the number. To make QuickBooks agree, rename the closed customer to "<unit>-1 - …" and the live one to "<unit> - …".',
		});
	}
	const dupLive = notes.filter((n) => n.kind === 'duplicate-live');
	if (dupLive.length > 0) {
		findings.push({
			severity: 'warning',
			code: 'customer_duplicate_live',
			title: `${fileName}: ${dupLive.length} unit${dupLive.length === 1 ? ' has' : 's have'} more than one LIVE QuickBooks customer`,
			detail: dupLive.map((n) => n.message).join('; '),
			fix: 'In QuickBooks, mark the previous owner "(CLOSED)" (or make it inactive) so only one customer is live per unit, then re-export the IIF.',
		});
	}
	if (fromBillTo.length > 0) {
		findings.push({
			severity: 'info',
			code: 'customer_address_from_bill_to',
			title: `${fileName}: ${fromBillTo.length} unit address${fromBillTo.length === 1 ? '' : 'es'} taken from bill-to (no ship-to)`,
			detail: `The bill-to block names the unit's own house number, so that street is used as the property address. ${listNames(fromBillTo)}`,
			fix: 'Check these after the import; add the ship-to address in QuickBooks to make it explicit.',
		});
	}
	if (closedNoDate.length > 0) {
		findings.push({
			severity: 'info',
			code: 'customer_closed_no_date',
			title: `${fileName}: ${closedNoDate.length} closed customer${closedNoDate.length === 1 ? '' : 's'} without a full close date`,
			detail: `Closed, with no close date recorded (a month/day with no year is never guessed). ${listNames(closedNoDate)}`,
			fix: 'To record the date, put the full date in the QuickBooks name — e.g. "(CLSD 6/26/2025)" — or set it on the account after the import.',
		});
	}
	return findings;
};

/**
 * Build the full pre-import reconciliation report. Pure + synchronous — safe
 * to call in the UI thread on file selection (12k-transaction bundles parse
 * in well under a second).
 */
export function buildQbPreflightReport(files: QbPreflightFile[]): QbPreflightReport {
	const findings: QbPreflightFinding[] = [];
	const fileSummaries: QbPreflightFileSummary[] = [];

	// ── Parse journals, dedup across files ─────────────────────────────────
	const entriesById = new Map<string, ParsedEntry>();
	const monthCounts: Record<string, number> = {};
	const typeCounts: Record<string, number> = {};
	let duplicateTransactions = 0;
	const conflictingIds: string[] = [];

	for (const f of files) {
		if (f.type !== 'journal_csv') continue;
		const { entries, errors } = parseJournalRows(parseCsv(f.text));
		const months = entries.map((e) => monthKeyFromMs(e.postedOn)).sort();
		fileSummaries.push({
			name: f.name,
			type: f.type,
			count: entries.length,
			errors,
			firstMonth: months[0],
			lastMonth: months[months.length - 1],
		});
		if (errors.length > 0) {
			findings.push({
				severity: 'blocking',
				code: 'journal_parse_errors',
				title: `${f.name}: ${errors.length} parse error${errors.length === 1 ? '' : 's'}`,
				detail: errors.slice(0, 3).join('; '),
				fix: `Re-export this file from QuickBooks (${JOURNAL_EXPORT_PATH} → Excel → "a comma separated values (.csv) file"). ${REPLACE_HINT}`,
			});
		}
		for (const e of entries) {
			const prior = entriesById.get(e.transactionId);
			if (prior) {
				duplicateTransactions++;
				if (entrySignature(prior) !== entrySignature(e)) conflictingIds.push(e.transactionId);
				continue;
			}
			entriesById.set(e.transactionId, e);
			const mk = monthKeyFromMs(e.postedOn);
			monthCounts[mk] = (monthCounts[mk] ?? 0) + 1;
			typeCounts[e.type || 'Unknown'] = (typeCounts[e.type || 'Unknown'] ?? 0) + 1;
		}
	}
	const journalEntries = [...entriesById.values()];
	const journalFiles = files.filter((f) => f.type === 'journal_csv');

	const hasDetailFiles = files.some((f) => f.type === 'transactions_csv' || f.type === 'vendor_transactions_csv');
	const hasIif = files.some((f) => f.type === 'iif');
	if (journalFiles.length === 0 && !hasDetailFiles && hasIif) {
		// Structure-only bundle: just the IIF lists (chart, customers, vendors)
		// with no transactions — a legitimate "import the structure" run.
		findings.push({
			severity: 'info',
			code: 'structure_only',
			title: 'Structure-only import — no transactions',
			detail: 'Only the IIF lists will import: chart of accounts, customers, and vendors. Add Journal/AR/AP CSVs to rebuild the books too.',
		});
	} else if (journalFiles.length === 0) {
		// /start rejects journal-less bundles too — but that lands AFTER the
		// batch is created and the files uploaded. Catch it here so the user
		// never gets a green report on a bundle the server will refuse.
		findings.push({
			severity: 'blocking',
			code: 'journal_missing',
			title: 'A QuickBooks Journal CSV is required',
			detail: 'The Transactions/Vendor reports only enrich the journal-derived ledger — without a Journal file nothing imports.',
			fix: `In QuickBooks: ${JOURNAL_EXPORT_PATH}, set the date range to cover ALL activity (one export per year keeps files manageable), then Excel → "a comma separated values (.csv) file". ${REPLACE_HINT}`,
		});
	} else if (journalEntries.length === 0) {
		findings.push({
			severity: 'blocking',
			code: 'journal_empty',
			title: 'No journal transactions parsed',
			detail: 'The selected Journal CSV(s) contained no importable transactions.',
			fix: `Re-export the Journal from QuickBooks (${JOURNAL_EXPORT_PATH} → Excel → .csv) — the current file may be an empty or filtered report. ${REPLACE_HINT}`,
		});
	}
	if (conflictingIds.length > 0) {
		findings.push({
			severity: 'blocking',
			code: 'duplicate_conflict',
			title: `${conflictingIds.length} Trans # appear in multiple files with DIFFERENT content`,
			detail: `e.g. Trans #${conflictingIds.slice(0, 5).join(', #')} — overlapping year exports should be identical.`,
			fix: `Re-export the affected years from QuickBooks (${JOURNAL_EXPORT_PATH}) so the overlapping files agree — the importer keeps whichever version it sees first, which is not deterministic. ${REPLACE_HINT}`,
		});
	} else if (duplicateTransactions > 0) {
		findings.push({
			severity: 'info',
			code: 'duplicate_overlap',
			title: `${duplicateTransactions} overlapping transactions across files (identical — deduplicated)`,
			detail: 'QuickBooks year exports often spill into the next January; identical duplicates import once.',
		});
	}

	// ── Month-coverage continuity ──────────────────────────────────────────
	// Gap ranges are INCLUSIVE on both ends ('from' = first empty month,
	// 'to' = last empty month) — same convention as the server-side
	// findJournalCoverageGaps guard.
	const monthsPresent = Object.keys(monthCounts).sort();
	const gapMonths = new Set<string>();
	if (monthsPresent.length > 1) {
		const gaps: Array<{ from: string; to: string }> = [];
		const last = monthsPresent[monthsPresent.length - 1];
		let gapStart: string | null = null;
		for (let ym = nextMonth(monthsPresent[0]); ym !== last; ym = nextMonth(ym)) {
			if (!monthCounts[ym]) {
				gapStart = gapStart ?? ym;
				gapMonths.add(ym);
			} else if (gapStart) {
				gaps.push({ from: gapStart, to: prevMonth(ym) });
				gapStart = null;
			}
		}
		if (gapStart) gaps.push({ from: gapStart, to: prevMonth(last) });
		for (const gap of gaps) {
			const gapYearFrom = gap.from.split('-')[0];
			const gapYearTo = gap.to.split('-')[0];
			// A journal file whose ENTIRE range sits inside the gap's calendar
			// year(s) is almost certainly the truncated export.
			const suspect = fileSummaries.find((fs): fs is QbPreflightFileSummary & { firstMonth: string; lastMonth: string } =>
				fs.type === 'journal_csv' && fs.firstMonth != null && fs.lastMonth != null
				&& fs.firstMonth >= `${gapYearFrom}-01` && fs.lastMonth <= `${gapYearTo}-12`);
			findings.push({
				severity: 'blocking',
				code: 'coverage_gap',
				title: `Journal coverage gap: ${monthLabel(gap.from)} – ${monthLabel(gap.to)}`,
				detail: `No journal transactions in this range — a year export is truncated or missing.${suspect ? ` "${suspect.name}" only covers ${monthLabel(suspect.firstMonth)} – ${monthLabel(suspect.lastMonth)}.` : ''}`,
				fix: `In QuickBooks: ${JOURNAL_EXPORT_PATH}, set the date range to 01/01/${gapYearFrom} – 12/31/${gapYearTo}, Refresh, then Excel → "a comma separated values (.csv) file". ${suspect ? `Click "Back to files", remove "${suspect.name}", add the new export, and Check files again` : 'Click "Back to files", add the new export, and Check files again'} — your other files stay loaded and nothing has been imported.`,
			});
		}
	}

	// ── Detail-report tie-outs ─────────────────────────────────────────────
	// Learn the AR/AP GL codes from the detail files' own Account column so the
	// comparison is encoding- and chart-agnostic.
	const journalNetByMonthForCodes = (codes: Set<string>): Map<string, number> => {
		const out = new Map<string, number>();
		if (codes.size === 0) return out;
		for (const e of journalEntries) {
			let net = 0;
			for (const l of e.lines) {
				if (codes.has(l.accountCode)) net += l.debitCents - l.creditCents;
			}
			if (net !== 0) {
				const mk = monthKeyFromMs(e.postedOn);
				out.set(mk, (out.get(mk) ?? 0) + net);
			}
		}
		return out;
	};

	const correctable = { ar: 0, ap: 0 };
	const runDetailTieOut = (
		fileType: 'transactions_csv' | 'vendor_transactions_csv',
		label: string,
		code: string,
		sign: 1 | -1,
	): QbPreflightDetailStats | undefined => {
		const detailFiles = files.filter((f) => f.type === fileType);
		if (detailFiles.length === 0) return undefined;

		const detailByMonth = new Map<string, number>();
		const glCodes = new Set<string>();
		const stats: QbPreflightDetailStats = { rows: 0, parties: 0, partiesWithBalance: 0, openBalanceCents: 0 };
		for (const f of detailFiles) {
			const parsed = fileType === 'transactions_csv' ? parseTransactionsCsv(f.text) : parseVendorCsv(f.text);
			let rows = 0;
			const rowMonths: string[] = [];
			for (const section of parsed.sections) {
				stats.parties++;
				let partyCents = 0;
				for (const t of section.transactions) {
					rows++;
					partyCents += Math.round(t.amount * 100);
					// Rows whose Trans # is absent from the journal export — exactly
					// what auto-correction can synthesize.
					if (t.transId && !entriesById.has(t.transId)) {
						correctable[fileType === 'transactions_csv' ? 'ar' : 'ap']++;
					}
					const mk = monthKeyFromDateStr(t.dateStr);
					if (!mk) continue;
					rowMonths.push(mk);
					detailByMonth.set(mk, (detailByMonth.get(mk) ?? 0) + Math.round(t.amount * 100));
					const acctCode = parseAccountCell(t.account ?? '').code;
					if (acctCode) glCodes.add(acctCode);
				}
				if (partyCents !== 0) stats.partiesWithBalance++;
				stats.openBalanceCents += partyCents;
			}
			stats.rows += rows;
			rowMonths.sort();
			fileSummaries.push({
				name: f.name,
				type: f.type,
				count: rows,
				errors: parsed.errors,
				firstMonth: rowMonths[0],
				lastMonth: rowMonths[rowMonths.length - 1],
			});
			if (parsed.errors.length > 0) {
				findings.push({
					severity: 'warning',
					code: `${code}_parse_errors`,
					title: `${f.name}: ${parsed.errors.length} rows skipped`,
					detail: parsed.errors.slice(0, 3).join('; '),
				});
			}
			if (rows === 0) {
				findings.push({
					severity: 'blocking',
					code: `${code}_empty`,
					title: `${f.name}: no ${label} rows parsed`,
					detail: 'The report layout was not recognized — check the file type assignment.',
					fix: `Confirm the file-type chip on "${f.name}" is correct, or re-export it from QuickBooks (${fileType === 'transactions_csv' ? 'Reports → Customers & Receivables → Customer Balance Detail' : 'Reports → Vendors & Payables → Vendor Balance Detail'} → export to CSV). ${REPLACE_HINT}`,
				});
			}
		}

		if (journalEntries.length > 0) {
			const journalByMonth = journalNetByMonthForCodes(glCodes);
			const { mismatches, journalTotal, detailTotal } = monthlyTieOut(journalByMonth, detailByMonth, sign);
			if (mismatches.length > 0) {
				findings.push({
					severity: 'blocking',
					code: `${code}_mismatch`,
					title: `${label} doesn't reconcile in ${mismatches.length} month${mismatches.length === 1 ? '' : 's'}`,
					detail: describeMismatches(mismatches, journalTotal, detailTotal, label),
					fix: buildMismatchFix(mismatches, monthsPresent, gapMonths),
				});
			} else if (detailByMonth.size > 0) {
				findings.push({
					severity: 'info',
					code: `${code}_ok`,
					title: `${label} ties out month-by-month (${fmtUsd(detailTotal)})`,
				});
			}
		}
		return stats;
	};

	const arStats = runDetailTieOut('transactions_csv', 'Accounts Receivable', 'ar', 1);
	const apStats = runDetailTieOut('vendor_transactions_csv', 'Accounts Payable', 'ap', -1);

	// ── IIF structure stats (what the lists import will create) ────────────
	const iifFiles = files.filter((f) => f.type === 'iif');
	let structureStats: QbPreflightStats['structure'];
	if (iifFiles.length > 0) {
		structureStats = { glAccounts: 0, customers: 0, vendors: 0, vendorTaxIds: 0 };
		for (const f of iifFiles) {
			const lists = parseIIF(f.text);
			structureStats.glAccounts += lists.accounts.length;
			structureStats.customers += lists.customers.length;
			structureStats.vendors += lists.vendors.length;
			structureStats.vendorTaxIds += lists.vendors.filter((v) => ((v.raw.TAXID || '').trim()).length > 0).length;
			findings.push(...customerAddressFindings(f.name, lists.customers));
		}
	}

	// ── IIF ending-balance tie-out + bank-balance stats ────────────────────
	let bankStats: QbPreflightBankStat[] | undefined;
	if (iifFiles.length > 0 && journalEntries.length > 0) {
		// Journal ending sums per account code.
		const journalByCode = new Map<string, number>();
		for (const e of journalEntries) {
			for (const l of e.lines) {
				if (!l.accountCode) continue;
				journalByCode.set(l.accountCode, (journalByCode.get(l.accountCode) ?? 0) + l.debitCents - l.creditCents);
			}
		}
		// Parse each IIF exactly once; reuse for both the summary and tie-out.
		const accountsByFile = iifFiles.map((f) => ({ file: f, accounts: parseIifAccountBalances(f.text) }));
		for (const { file, accounts } of accountsByFile) {
			fileSummaries.push({ name: file.name, type: file.type, count: accounts.length, errors: [] });
		}
		const accounts = accountsByFile.flatMap((a) => a.accounts);
		const checked = accounts.filter((a) => ['BANK', 'AR', 'AP'].includes(a.accountType) && a.code);
		bankStats = checked
			.filter((a) => a.accountType === 'BANK')
			.map((a) => {
				const journalCents = journalByCode.get(a.code) ?? 0;
				return { code: a.code, name: a.name, journalCents, qbCents: a.balanceCents, matches: Math.abs(journalCents - a.balanceCents) <= CENTS_TOLERANCE };
			});
		const mismatched = checked.filter((a) => Math.abs((journalByCode.get(a.code) ?? 0) - a.balanceCents) > CENTS_TOLERANCE);
		if (mismatched.length > 0) {
			findings.push({
				severity: 'blocking',
				code: 'iif_balance_mismatch',
				title: `${mismatched.length} account balance${mismatched.length === 1 ? '' : 's'} won't match QuickBooks`,
				detail: mismatched.slice(0, 6)
					.map((a) => `${a.code} ${a.name}: journal projects ${fmtUsd(journalByCode.get(a.code) ?? 0)}, QuickBooks says ${fmtUsd(a.balanceCents)}`)
					.join('; '),
				fix: 'These usually resolve once the journal issues above are fixed — replace those exports and Check files again. If balances still mismatch while AR/AP tie out green, re-export the IIF (File → Utilities → Export → Lists to IIF Files…) and replace it here; it may be from a different date than the reports.',
			});
		} else if (checked.length > 0) {
			findings.push({
				severity: 'info',
				code: 'iif_balance_ok',
				title: `All ${checked.length} bank/AR/AP balances match QuickBooks`,
			});
		}
	}

	const verdict = findings.some((f) => f.severity === 'blocking')
		? 'blocked'
		: findings.some((f) => f.severity === 'warning') ? 'warnings' : 'ok';

	return {
		verdict,
		findings,
		files: fileSummaries,
		stats: {
			journal: {
				uniqueTransactions: journalEntries.length,
				duplicateTransactions,
				byType: typeCounts,
				firstMonth: monthsPresent[0],
				lastMonth: monthsPresent[monthsPresent.length - 1],
			},
			ar: arStats,
			ap: apStats,
			banks: bankStats,
			structure: structureStats,
			correctable: (correctable.ar > 0 || correctable.ap > 0) ? correctable : undefined,
		},
		journal: {
			uniqueTransactions: journalEntries.length,
			duplicateTransactions,
			months: monthCounts,
		},
	};
}
